import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Annotation } from './annotation';
import styles from './sidebar-annotations.module.css';
import { DraggableIcon } from '../../../../icons/draggable';
import { getAnnotationIcon } from '../../util/get-annotation-icon';

type SidebarAnnotationProps = {
	annotationType: Annotation['type'];
	isDragDisabled?: boolean;
	/** Called where the drag is released; the receiver decides if it hit a page. */
	onDrop: (
		type: Annotation['type'],
		clientX: number,
		clientY: number,
	) => void;
};

function getAnnotationLabel(annotationType: Annotation['type']) {
	switch (annotationType) {
		case 'manuscript':
			return 'Firma';
		case 'full_name':
			return 'Nombre';
		case 'initials':
			return 'Iniciales';
		case 'date':
			return 'Fecha';
		case 'text':
			return 'Texto';
	}
}

/**
 * Palette item dragged with pointer events (no HTML5 drag and drop): the pointer is
 * captured on press, a ghost follows it, and the drop point is handed to `onDrop`.
 */
export const SidebarAnnotation = (props: SidebarAnnotationProps) => {
	const [ghost, setGhost] = useState<{ x: number; y: number } | null>(null);
	// Source of truth for "a drag is in progress": handlers can run before React
	// re-renders with the new ghost state.
	const isDraggingRef = useRef(false);

	const label = getAnnotationLabel(props.annotationType);

	return (
		<div
			style={{
				height: 56,
				touchAction: 'none',
			}}
			onPointerDown={(event) => {
				if (props.isDragDisabled || event.button !== 0) return;
				event.preventDefault();
				try {
					// Keeps move/up events coming here while the pointer is over the page.
					event.currentTarget.setPointerCapture(event.pointerId);
				} catch {
					// The pointer is already gone (e.g. released during the press).
				}
				isDraggingRef.current = true;
				setGhost({ x: event.clientX, y: event.clientY });
			}}
			onPointerMove={(event) => {
				if (isDraggingRef.current) {
					setGhost({ x: event.clientX, y: event.clientY });
				}
			}}
			onPointerUp={(event) => {
				if (!isDraggingRef.current) return;
				isDraggingRef.current = false;
				setGhost(null);
				props.onDrop(
					props.annotationType,
					event.clientX,
					event.clientY,
				);
			}}
			onPointerCancel={() => {
				isDraggingRef.current = false;
				setGhost(null);
			}}
		>
			<div className={styles['sidebar-annotation']}>
				<DraggableIcon />
				<div className={styles['icon-and-label']}>
					{getAnnotationIcon(props.annotationType)}
					<span className={styles['annotation-label']}>{label}</span>
				</div>
			</div>
			{ghost &&
				createPortal(
					<div
						className={styles['drag-ghost']}
						style={{ left: ghost.x, top: ghost.y }}
						aria-hidden
					>
						{getAnnotationIcon(props.annotationType)}
						<span className={styles['annotation-label']}>
							{label}
						</span>
					</div>,
					document.body,
				)}
		</div>
	);
};
